import { getCountryCallingCode, parsePhoneNumber, type CountryCode, type PhoneNumber } from 'libphonenumber-js';

/** A recipient the user typed, resolved to the chat id it is saved as. */
export interface TypedRecipient {
    chatId: string;
    /** What the user sees before adding it, e.g. "+972 54-552-1186". */
    label: string;
}

const parse = (text: string, country?: string): PhoneNumber | undefined => {
    try {
        return parsePhoneNumber(text, country?.toUpperCase() as CountryCode | undefined);
    } catch {
        return undefined;
    }
};

/** Is `digits` written the way `country` writes its OWN numbers: a leading 0, or 10 digits in a +1 country (no trunk 0)? */
const writtenLocally = (written: string, digits: string, country: string): boolean => {
    if (written.startsWith('0')) return true;
    try {
        return getCountryCallingCode(country.toUpperCase() as CountryCode) === '1' && digits.length === 10;
    } catch {
        return false;
    }
};

/**
 * THE one place a typed recipient becomes a chat id. A WhatsApp id is the number
 * in INTERNATIONAL form, so a local number ("054-552-1186") is read in the user's
 * own country — never saved as typed: "0545521186@c.us" is a chat that does not
 * exist, and an agent bound to it never answers (prod 2026-10-19).
 *
 *   - a pasted id: a group / channel / @lid id as is; a phone id ("…@c.us",
 *     "…@s.whatsapp.net") is re-read as the number before the "@"
 *   - more than 15 digits → a group id (no phone number is that long)
 *   - "+…" / "00…" → international as written
 *   - written locally (see writtenLocally) → the user's country; anything else is
 *     international without the + ("972545521186"). Trying the local reading
 *     first for everything misreads those: in Germany "972545521186" is a valid
 *     local number too.
 *
 * null = not something we can send to: the caller asks for the country code.
 */
export function resolveTypedRecipient(input: string, country?: string): TypedRecipient | null {
    const [text, server] = input.trim().split('@');
    if (!text) return null;
    if (server !== undefined && !/^(c\.us|s\.whatsapp\.net)$/.test(server)) return { chatId: input.trim(), label: input.trim() };
    const written = text.replace(/^00/, '+');
    const digits = written.replace(/\D/g, '');
    if (digits.length > 15) return { chatId: `${digits}@g.us`, label: digits };
    const international = parse(`+${digits}`);
    const local = !written.startsWith('+') && country ? parse(written, country) : undefined;
    const order = local && writtenLocally(written, digits, country!) ? [local, international] : [international, local];
    const phone = order.find((p) => p?.isValid()) ?? order.find((p) => p?.isPossible());
    if (!phone) return null;
    return {
        chatId: `${phone.number.slice(1)}@c.us`,
        label: phone.formatInternational(),
    };
}

/** The country of a phone number in any form we store ("972545521186", "+972 54…"). */
export function countryOfPhone(phone: string | undefined | null): string | undefined {
    const digits = String(phone ?? '').replace(/\D/g, '');
    return digits ? parse(`+${digits}`)?.country?.toLowerCase() : undefined;
}

/**
 * Formats a WhatsApp serialized id (e.g. "972548275828@c.us") as an
 * international phone number ("+972 54-827-5828"). Returns null for ids
 * that aren't plain phone numbers (@lid ids, device-indexed ids, groups
 * with non-numeric parts) — callers chain `|| fallback`.
 */
export function formatWaIdAsInternationalPhone(serializedId: string): string | null {
    try {
        if (!serializedId) return '';
        // WhatsApp internal ids (e.g. 147931726356493@lid) are not phone numbers
        if (serializedId.endsWith('@lid')) return null;
        const rawNumber = serializedId.split('@')[0];
        // Strip device index (972548275828:2)
        const phoneOnly = rawNumber.includes(':') ? rawNumber.split(':')[0] : rawNumber;
        if (!/^\d+$/.test(phoneOnly)) return null;
        return parsePhoneNumber('+' + phoneOnly).formatInternational();
    } catch {
        // Expected for non-phone WhatsApp ids
        return null;
    }
}

/**
 * A recipient label for display: a real name is kept, but a label that is only
 * digits (a number typed at compose time is stored as "14155550123") is written
 * the way a phone number is written ("+1 415 555 0123"), from the label itself
 * or the chat id. Anything unparseable falls back to the label unchanged.
 */
export function nameOrFormattedPhone(name: string | null | undefined, serializedId?: string | null): string {
    const label = String(name ?? '').trim();
    if (label && !/^\+?[\d\s-]+$/.test(label)) return label;
    // Only a person's chat id is a phone number (group / channel / @lid ids are digits too).
    if (serializedId) return (serializedId.endsWith('@c.us') && formatWaIdAsInternationalPhone(serializedId)) || label;
    const digits = label.replace(/\D/g, '');
    return (digits.length >= 6 && formatWaIdAsInternationalPhone(`${digits}@c.us`)) || label;
}

/** A WhatsApp phone id's digits ("5551987654321@c.us" → "5551987654321"); undefined for groups, channels and @lid ids. */
const phoneDigitsOfChatId = (chatId: string): string | undefined => {
    const [digits, server] = chatId.split('@');
    return /^\d+$/.test(digits ?? '') && (server === 'c.us' || server === 's.whatsapp.net') ? digits : undefined;
};

/**
 * Recipients that may be MISSING the user's own country code (#873): a Brazilian
 * user's "51987654321" (DDD 51 + 9 digits) was saved as is, which WhatsApp reads as
 * a Peruvian number (+51) and answers "not on WhatsApp".
 *
 * A suspect is a phone id that is a valid MOBILE number read as the user's country's
 * LOCAL number, and is not already a valid number carrying that country's code (so a
 * local number whose area code happens to equal the country code — Brazil's DDD 55,
 * "55987654321" — is still caught). Which rules make a number "local mobile" is
 * libphonenumber's, not ours.
 *
 * It is a SUSPICION, never a verdict: "51987654321" is equally a genuine Peruvian
 * mobile (15 such numbers delivered in the #873 account), so the caller must offer
 * "add the code" and "keep as is" and never rewrite on its own.
 *
 * Async + "eager": number TYPES need libphonenumber's `mobile` metadata (~100KB raw /
 * ~25KB gzipped, vs ~160KB for `max`), imported here and not at the top so the type
 * data stays out of every other consumer's path. The extension build disables chunk
 * splitting (a real async chunk would be fetched from "/" of the host page, not from the
 * extension), so like every other dynamic import in the codebase it is `eager`.
 */
export async function findMissingCountryCode(chatIds: readonly string[], country: string | undefined): Promise<{ callingCode: string; suspects: string[] } | undefined> {
    if (!country) return undefined;
    let callingCode: string;
    try {
        callingCode = getCountryCallingCode(country.toUpperCase() as CountryCode);
    } catch {
        return undefined;
    }
    const candidates = chatIds.filter((chatId) => phoneDigitsOfChatId(chatId) !== undefined);
    if (!candidates.length) return undefined;
    const { parsePhoneNumber: parseWithTypes } = await import(/* webpackMode: "eager" */ 'libphonenumber-js/mobile');
    const read = (text: string, region?: CountryCode): PhoneNumber | undefined => {
        try {
            return parseWithTypes(text, region);
        } catch {
            return undefined;
        }
    };
    const suspects = candidates.filter((chatId) => {
        const digits = phoneDigitsOfChatId(chatId)!;
        if (digits.startsWith(callingCode) && read(`+${digits}`)?.isValid()) return false;
        const local = read(digits, country.toUpperCase() as CountryCode);
        const type = local?.isValid() ? local.getType() : undefined;
        return (type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE') && local?.nationalNumber === digits;
    });
    return suspects.length ? { callingCode, suspects } : undefined;
}

/** `chatId` with `callingCode` put in front of its number (groups / channels / @lid ids untouched). */
export function addCountryCode(chatId: string, callingCode: string): string {
    const digits = phoneDigitsOfChatId(chatId);
    return digits ? `${callingCode}${digits}@${chatId.split('@')[1]}` : chatId;
}
